import React, { useEffect, useMemo, useState } from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonRow,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react';
import { IonIcon } from '@ionic/react';
import { Capacitor } from '@capacitor/core';
import { useSelector } from 'react-redux';
import { Printer } from '@bcyesil/capacitor-plugin-printer';
import { createOutline, eyeOutline, printOutline, cloudUploadOutline, trashOutline, personAddOutline } from 'ionicons/icons';
import Select from 'react-select';
import { API } from '../../constants/system';
import { getCashRegister, getDominio, getLoginApp, getToken } from '../../utils/functions';
import {
  createCustomerOffline,
  createOfflineInvoice,
  deleteCustomerOfflineById,
  deleteOfflineInvoice,
  initdb,
  markOfflineInvoiceSyncStatus,
  queryAllCustomersOffline,
  queryLocalCreatedCustomersOffline,
  queryAllOfflineInvoices,
  queryAllProductsOffline,
  queryOfflineInvoiceById,
  queryOfflineInvoiceLines,
  updateCustomerOfflineAfterSync,
  updateOfflineInvoice,
} from '../../data_base_local/dataservice';

const options2 = { style: 'currency' as const, currency: 'USD', useGrouping: false };
const _numberFormat = new Intl.NumberFormat('en-US', options2);

interface OfflineCustomer {
  id: number;
  contact_id: string;
  name: string;
  mobile?: string;
  type: string;
  customer_group_id: number | null;
  supplier_business_name?: string;
  selling_price_group_id?: number | null;
  is_local_created?: number;
  server_contact_db_id?: number | null;
}

interface OfflineProduct {
  id: number;
  product_id: number;
  name: string;
  type: string;
  quantity: number;
  current_price: number;
  current_price_discount: number;
  discount_amount: number;
  discount_type: string;
  enable_stock: number | boolean;
  unit_id: number | null;
  qty_available: number;
  dpp_inc_tax: number;
  sub_sku: string;
  data_price_group?: Array<{
    id: number | null;
    name: string;
    price_inc_tax: number;
    price_type?: string;
  }> | string;
}

interface CartItem extends OfflineProduct {
  quantitySelected: number;
}

interface OfflineInvoice {
  id: number;
  local_uuid: string;
  contact_id: number;
  customer_name: string;
  status_sale: string;
  total: number;
  location_id: number | null;
  notes: string;
  payload_json?: string;
  status_sync: 'pending' | 'synced' | 'error' | 'syncing';
  server_response?: string;
  server_transaction_id?: string | null;
  error_message?: string;
  created_at?: string;
  updated_at?: string;
  lines_total?: number;
}

interface OfflineInvoiceLine {
  id: number;
  invoice_id: number;
  variation_id: number;
  product_id: number;
  product_name: string;
  sku: string;
  qty: number;
  unit_price: number;
  unit_price_inc_tax: number;
  discount_type: string;
  discount_amount: number;
  unit_id: number;
  dpp_inc_tax: number;
  enable_stock: number;
  product_type: string;
  line_json?: string;
}

type OfflineSyncMode = 'preventa' | 'cotizacion';

const PosOffline: React.FC = () => {
  const data_default = useSelector((state: any) => state?.data?.data_default || {});
  const reduxCashRegisterUtil = data_default?.cash_register_util || {};
  const cashRegisterData = getCashRegister() || {};
  const currencyprimary = reduxCashRegisterUtil?.currencyprimary || cashRegisterData?.currencyprimary || '$';
  const formatPrimaryCurrency = (amount: any) => {
    return _numberFormat.format(Number(amount || 0)).replace('$', currencyprimary);
  };

  const [activeTab, setActiveTab] = useState<'draft' | 'invoices'>('draft');
  const [customers, setCustomers] = useState<OfflineCustomer[]>([]);
  const [products, setProducts] = useState<OfflineProduct[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [offlineInvoices, setOfflineInvoices] = useState<OfflineInvoice[]>([]);
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null);
  const [draftSyncMode, setDraftSyncMode] = useState<OfflineSyncMode>('cotizacion');
  const [notes, setNotes] = useState<string>('');
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState<boolean>(false);
  const [newCustomerContactId, setNewCustomerContactId] = useState<string>('');
  const [newCustomerName, setNewCustomerName] = useState<string>('');
  const [newCustomerMobile, setNewCustomerMobile] = useState<string>('');
  const [newCustomerBusinessName, setNewCustomerBusinessName] = useState<string>('');
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState<boolean>(false);
  const [modalInvoice, setModalInvoice] = useState<OfflineInvoice | null>(null);
  const [modalInvoiceLines, setModalInvoiceLines] = useState<OfflineInvoiceLine[]>([]);
  const [syncCustomerMessage, setSyncCustomerMessage] = useState<string>('');
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [isSyncingCustomers, setIsSyncingCustomers] = useState<boolean>(false);

  const getBaseUrl = () => {
    const dominioRaw = typeof getDominio === 'function' ? getDominio() : '';
    const dominioValue = (dominioRaw ?? '').toString();
    const dominio: string[] = dominioValue.split('http');
    const storedDomain = window.localStorage.getItem('ari_app_dominio') || '';
    let url = "";

    if (!storedDomain) {
      return url;
    }

    if (dominio.length > 1) {
      url = storedDomain;
    } else {
      url = "https://" + storedDomain;
    }

    return url;
  };

  const refreshOfflineInvoices = async () => {
    const result = await queryAllOfflineInvoices();
    setOfflineInvoices((result?.values || []) as OfflineInvoice[]);
  };

  const loadLocalData = async () => {
    await initdb();
    const customersResult = await queryAllCustomersOffline();
    const productsResult = await queryAllProductsOffline();
    setCustomers((customersResult?.values || []) as OfflineCustomer[]);
    setProducts((productsResult?.values || []) as OfflineProduct[]);
    await refreshOfflineInvoices();
  };

  useEffect(() => {
    loadLocalData().catch((error) => {
      console.log('Error cargando POS offline', error);
    });
  }, []);

  useIonViewWillEnter(() => {
    loadLocalData().catch((error) => {
      console.log('Error recargando POS offline', error);
    });
  });

  const selectedCustomer = useMemo(() => {
    return customers.find((customer) => String(customer.id) === String(selectedCustomerId)) || null;
  }, [customers, selectedCustomerId]);

  const parseLineJson = (line: OfflineInvoiceLine) => {
    if (!line.line_json) {
      return null;
    }

    try {
      return JSON.parse(line.line_json);
    } catch (error) {
      return null;
    }
  };

  const resolveProductPriceForCustomer = (product: OfflineProduct, customer: OfflineCustomer | null) => {
    if (!customer || !customer.selling_price_group_id) {
      return Number(product.current_price_discount || product.current_price || 0);
    }

    let priceGroups: any[] = [];
    if (Array.isArray(product.data_price_group)) {
      priceGroups = product.data_price_group;
    } else if (typeof product.data_price_group === 'string' && product.data_price_group.trim() !== '') {
      try {
        priceGroups = JSON.parse(product.data_price_group);
      } catch (error) {
        priceGroups = [];
      }
    }

    const matchedPriceGroup = priceGroups.find((priceGroup) => String(priceGroup?.id ?? '') === String(customer.selling_price_group_id));
    if (matchedPriceGroup && matchedPriceGroup.price_inc_tax != null) {
      return Number(matchedPriceGroup.price_inc_tax || 0);
    }

    return Number(product.current_price_discount || product.current_price || 0);
  };

  const customerOptions = useMemo(() => {
    return customers.map((customer) => ({
      value: String(customer.id),
      label: `${customer.contact_id || ''} - ${customer.name || ''} - ${customer.supplier_business_name || ''} -${customer.selling_price_group_id ? ` | PG ${customer.selling_price_group_id}` : ''}`,
    }));
  }, [customers]);

  const productOptions = useMemo(() => {
    return products.map((product) => ({
      value: String(product.id),
      label: `${product.name || ''} | ${product.sub_sku || ''} | ${formatPrimaryCurrency(product.current_price_discount || 0)} | stock ${product.qty_available || 0}`,
    }));
  }, [products, currencyprimary]);

  const cartTotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      return sum + (Number(item.current_price_discount || 0) * Number(item.quantitySelected || 0));
    }, 0);
  }, [cartItems]);

  const getInvoicePayload = (invoice: OfflineInvoice) => {
    if (!invoice.payload_json) {
      return null;
    }

    try {
      return JSON.parse(invoice.payload_json);
    } catch (error) {
      return null;
    }
  };

  const getInvoiceKind = (invoice: OfflineInvoice): OfflineSyncMode => {
    const payload = getInvoicePayload(invoice);
    const status = String(payload?.status || invoice.status_sale || '').toLowerCase();
    const typePreventa = Number(payload?.type_preventa || 0) === 1;

    if (typePreventa || status === 'draft') {
      return 'preventa';
    }

    return 'cotizacion';
  };

  const localCustomersCount = useMemo(() => {
    return customers.filter((customer: any) => Number(customer?.is_local_created || 0) === 1).length;
  }, [customers]);

  const resetDraft = () => {
    setEditingInvoiceId(null);
    setSelectedProductId('');
    setCartItems([]);
    setDraftSyncMode('cotizacion');
    setNotes('');
  };

  const saveLocalCustomer = async () => {
    if (!newCustomerName.trim()) {
      window.alert('Ingrese nombre del cliente');
      return;
    }

    if (!newCustomerMobile.trim()) {
      window.alert('Ingrese movil del cliente');
      return;
    }

    const createdId = await createCustomerOffline({
      contact_id: newCustomerContactId.trim() || `OFF-${Date.now()}`,
      name: newCustomerName.trim(),
      mobile: newCustomerMobile.trim(),
      type: 'customer',
      customer_group_id: null,
      supplier_business_name: newCustomerBusinessName.trim(),
      selling_price_group_id: null,
    });

    const customersResult = await queryAllCustomersOffline();
    const updatedCustomers = (customersResult?.values || []) as OfflineCustomer[];
    setCustomers(updatedCustomers);

    if (createdId) {
      setSelectedCustomerId(String(createdId));
    }

    setIsCustomerModalOpen(false);
    setNewCustomerContactId('');
    setNewCustomerName('');
    setNewCustomerMobile('');
    setNewCustomerBusinessName('');
  };

  const addSelectedProductToCart = () => {
    const product = products.find((item) => String(item.id) === String(selectedProductId));
    if (!product) {
      window.alert('Seleccione un producto');
      return;
    }

    const resolvedPrice = resolveProductPriceForCustomer(product, selectedCustomer);

    setCartItems((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        return prev.map((item) => item.id === product.id ? {
          ...item,
          quantitySelected: item.quantitySelected + 1,
          current_price: resolvedPrice,
          current_price_discount: resolvedPrice,
        } : item);
      }

      return [
        ...prev,
        {
          ...product,
          current_price: resolvedPrice,
          current_price_discount: resolvedPrice,
          quantitySelected: 1,
        }
      ];
    });
  };

  const updateCartQuantity = (productId: number, quantitySelected: number) => {
    if (quantitySelected <= 0) {
      setCartItems((prev) => prev.filter((item) => item.id !== productId));
      return;
    }

    setCartItems((prev) => prev.map((item) => item.id === productId ? {
      ...item,
      quantitySelected,
    } : item));
  };

  useEffect(() => {
    setCartItems((prev) => prev.map((item) => {
      const sourceProduct = products.find((product) => product.id === item.id) || item;
      const resolvedPrice = resolveProductPriceForCustomer(sourceProduct, selectedCustomer);

      return {
        ...item,
        current_price: resolvedPrice,
        current_price_discount: resolvedPrice,
      };
    }));
  }, [selectedCustomerId, products]);

  const buildOfflineOrderPayload = (syncMode: OfflineSyncMode = 'cotizacion') => {
    const login = getLoginApp() || {};
    const cashRegister = getCashRegister() || {};
    const locationId = cashRegister.location_id ?? login.location_id ?? null;

    const productsPayload = cartItems.map((item) => ({
      quantity: item.quantitySelected,
      variation_id: item.id,
      product_id: item.product_id,
      line_discount_type: item.discount_type || 'fixed',
      line_discount_amount: Number(item.discount_amount || 0),
      unit_price: Number(item.current_price || 0),
      unit_price_inc_tax: Number(item.current_price_discount || 0),
      item_tax: 0,
      tax_id: null,
      tax_rate_id: null,
      enable_stock: item.enable_stock === true || item.enable_stock === 1,
      product_type: item.type || 'single',
      base_unit_multiplier: 1,
      sub_unit_id: item.unit_id ?? 1,
      product_unit_id: item.unit_id ?? 1,
      dpp_inc_tax: Number(item.dpp_inc_tax || 0),
      unit_cost: Number(item.dpp_inc_tax || 0),
    }));

    const resolvedContactId = (() => {
      if (!selectedCustomer) {
        return null;
      }

      const serverContactDbId = Number(selectedCustomer.server_contact_db_id || 0);
      if (serverContactDbId > 0) {
        return serverContactDbId;
      }

      const contactCode = String(selectedCustomer.contact_id || '');
      const isLocalCustomer = Number(selectedCustomer.is_local_created || 0) === 1 || contactCode.startsWith('OFF-');
      if (isLocalCustomer) {
        return contactCode;
      }

      return selectedCustomer.id;
    })();

    const payload = {
      user_id: login.id ?? null,
      transaction_date: new Date().toISOString().slice(0, 19).replace('T', ' '),
      contact_id: resolvedContactId,
      status: 'quotation',
      is_quotation: 1,
      final_total: cartTotal,
      location_id: locationId,
      location_retiro: locationId,
      is_suspend: 0,
      is_credit_sale: 0,
      is_cataloge: 1,
      is_direct_sale: 0,
      typeOption: 'saveOrder',
      action: 'create',
      type_pasarela: 1,
      payment: [],
      is_return: 0,
      change_return: 0,
      change_return_secondary: 0,
      transaction_id: null,
      view_active: 'dashboard_pos/cart',
      commission_agent: null,
      discount_type: 'fixed',
      discount_amount: 0,
      tax_rate_id: null,
      tax_calculation_amount: 0,
      tax_amount: 0,
      additional_notes: notes || '',
      total_preview: cartTotal,
      customer_data: selectedCustomer ? {
        local_customer_id: selectedCustomer.id,
        contact_id: selectedCustomer.contact_id,
        name: selectedCustomer.name,
        mobile: selectedCustomer.mobile || null,
      } : null,
      products: productsPayload,
    };

    if (syncMode === 'preventa') {
      return {
        ...payload,
        status: 'draft',
        is_quotation: 0,
        type_preventa: 1,
        sub_status: null,
      };
    }

    return {
      ...payload,
      status: 'quotation',
      is_quotation: 1,
      type_preventa: null,
      sub_status: 'quotation',
    };
  };

  const buildOfflineInvoiceLines = (payloadProducts: any[]) => {
    return cartItems.map((item, index) => ({
      variation_id: item.id,
      product_id: item.product_id,
      product_name: item.name,
      sku: item.sub_sku || '',
      qty: Number(item.quantitySelected || 0),
      unit_price: Number(item.current_price || 0),
      unit_price_inc_tax: Number(item.current_price_discount || 0),
      discount_type: item.discount_type || 'fixed',
      discount_amount: Number(item.discount_amount || 0),
      unit_id: item.unit_id ?? 1,
      dpp_inc_tax: Number(item.dpp_inc_tax || 0),
      enable_stock: item.enable_stock === true || item.enable_stock === 1 ? 1 : 0,
      product_type: item.type || 'single',
      line_payload: payloadProducts[index] || null,
    }));
  };

  const printInvoice = (invoice: OfflineInvoice, lines: OfflineInvoiceLine[]) => {
    const safeCustomer = invoice.customer_name || 'Cliente no definido';
    const safeDate = invoice.created_at ? new Date(invoice.created_at).toLocaleString() : new Date().toLocaleString();
    const rowsHtml = lines.map((line) => {
      const subtotal = Number(line.unit_price_inc_tax || 0) * Number(line.qty || 0);
      return `
        <tr>
          <td style="padding:8px;border-bottom:1px solid #ddd;">${line.product_name || ''}</td>
          <td style="padding:8px;border-bottom:1px solid #ddd;">${line.sku || ''}</td>
          <td style="padding:8px;border-bottom:1px solid #ddd;text-align:center;">${Number(line.qty || 0).toFixed(2)}</td>
          <td style="padding:8px;border-bottom:1px solid #ddd;text-align:right;">${formatPrimaryCurrency(line.unit_price_inc_tax || 0)}</td>
          <td style="padding:8px;border-bottom:1px solid #ddd;text-align:right;">${formatPrimaryCurrency(subtotal)}</td>
        </tr>
      `;
    }).join('');

    const html = `
      <html>
        <head>
          <title>Factura #${invoice.id}</title>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <script>
            function triggerPrint() {
              try {
                window.focus();
                window.print();
              } catch (error) {
                // ignore and allow manual print with the button
              }
            }

            window.addEventListener('load', function () {
              setTimeout(function () {
                triggerPrint();
              }, 500);
            });

            window.addEventListener('afterprint', function () {
              setTimeout(function () {
                window.close();
              }, 300);
            });
          </script>
        </head>
        <body style="font-family: Arial, sans-serif; padding: 20px; color: #111;">
        <h2 style="margin:0 0 8px 0;">${getLoginApp()?.company ?? null}</h2>
          <h2 style="margin:0 0 8px 0;">${getInvoiceKind(invoice) === 'cotizacion' ? 'Cotización' : 'Pre-Venta'} #${invoice.id}</h2>
          <div style="margin-bottom: 14px;">Cliente: <strong>${safeCustomer}</strong></div>
          <div style="margin-bottom: 14px;">Fecha: ${safeDate}</div>
          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr>
                <th style="text-align:left;padding:8px;border-bottom:2px solid #333;">Producto</th>
                <th style="text-align:left;padding:8px;border-bottom:2px solid #333;">SKU</th>
                <th style="text-align:center;padding:8px;border-bottom:2px solid #333;">Cant</th>
                <th style="text-align:right;padding:8px;border-bottom:2px solid #333;">Precio</th>
                <th style="text-align:right;padding:8px;border-bottom:2px solid #333;">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
          <h3 style="text-align:right; margin-top: 18px;">Total: ${formatPrimaryCurrency(invoice.total || 0)}</h3>
        </body>
      </html>
    `;

    printTransaction(html);
  };

  const printTransaction = (html: string) => {
    if (Capacitor.isNativePlatform()) {
      Printer.print({ content: html, name: `factura-offline-${Date.now()}`, orientation: 'portrait' })
        .then((result: any) => {
          console.log('Impresion nativa OK', result);
        })
        .catch((error: any) => {
          console.error('Error impresion nativa', error);
          window.alert('No se pudo imprimir en el dispositivo.');
        });

      return;
    }

    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.srcdoc = html;

    const printArea = document.getElementById('print-area');
    if (printArea) {
      printArea.appendChild(printFrame);
    } else {
      document.body.appendChild(printFrame);
    }

    printFrame.onload = () => {
      try {
        printFrame.contentWindow?.focus();
        printFrame.contentWindow?.print();
      } catch (error) {
        console.error('Error al imprimir en web', error);
      }

      setTimeout(() => {
        if (printFrame.parentNode === document.body) {
          document.body.removeChild(printFrame);
        } else if (printFrame.parentNode) {
          printFrame.parentNode.removeChild(printFrame);
        }
      }, 3000);
    };
  };

  const saveOfflineInvoice = async (printAfterSave: boolean = false) => {
    if (!selectedCustomer) {
      window.alert('Seleccione un cliente');
      return;
    }

    if (cartItems.length === 0) {
      window.alert('Agregue al menos un producto al carrito');
      return;
    }

    const payload = buildOfflineOrderPayload(draftSyncMode);
    const lines = buildOfflineInvoiceLines(payload.products || []);
    const login = getLoginApp() || {};
    const invoiceData = {
      local_uuid: editingInvoiceId ? `offline_edit_${editingInvoiceId}` : `offline_${Date.now()}`,
      contact_id: selectedCustomer.id,
      customer_name: `${selectedCustomer.contact_id || ''} - ${selectedCustomer.name || ''}`,
      status_sale: payload.status || 'quotation',
      total: cartTotal,
      location_id: payload.location_id ?? login.location_id ?? null,
      notes: notes || '',
      payload,
      status_sync: 'pending',
      server_response: '',
      server_transaction_id: null,
      error_message: '',
    };

    let invoiceId = editingInvoiceId;
    if (editingInvoiceId) {
      await updateOfflineInvoice(editingInvoiceId, invoiceData, lines);
      window.alert(`Factura offline #${editingInvoiceId} actualizada`);
    } else {
      invoiceId = await createOfflineInvoice(invoiceData, lines);
      window.alert(`Factura offline guardada #${invoiceId}`);
    }

    await refreshOfflineInvoices();

    if (printAfterSave && invoiceId) {
      const savedInvoiceResult = await queryOfflineInvoiceById(Number(invoiceId));
      const savedLinesResult = await queryOfflineInvoiceLines(Number(invoiceId));
      const savedInvoice = (savedInvoiceResult?.values || [])[0] as OfflineInvoice;
      const savedLines = (savedLinesResult?.values || []) as OfflineInvoiceLine[];
      if (savedInvoice) {
        printInvoice(savedInvoice, savedLines);
      }
    }

    resetDraft();
    setActiveTab('invoices');
  };

  const openInvoiceModal = async (invoiceId: number) => {
    const invoiceResult = await queryOfflineInvoiceById(invoiceId);
    const linesResult = await queryOfflineInvoiceLines(invoiceId);
    const invoice = (invoiceResult?.values || [])[0] as OfflineInvoice;
    const lines = (linesResult?.values || []) as OfflineInvoiceLine[];

    if (!invoice) {
      window.alert('Factura no encontrada');
      return;
    }

    setModalInvoice(invoice);
    setModalInvoiceLines(lines);
    setIsInvoiceModalOpen(true);
  };

  const editOfflineInvoice = async (invoiceId: number) => {
    const invoiceResult = await queryOfflineInvoiceById(invoiceId);
    const linesResult = await queryOfflineInvoiceLines(invoiceId);
    const invoice = (invoiceResult?.values || [])[0] as OfflineInvoice;
    const lines = (linesResult?.values || []) as OfflineInvoiceLine[];

    if (!invoice) {
      window.alert('Factura no encontrada');
      return;
    }

    const mappedCartItems: CartItem[] = lines.map((line, index) => {
      const baseProduct = products.find((product) => String(product.id) === String(line.variation_id));
      return {
        ...(baseProduct || {
          id: Number(line.variation_id || index + 1),
          product_id: Number(line.product_id || 0),
          name: line.product_name || 'Producto',
          type: line.product_type || 'single',
          quantity: 1,
          current_price: Number(line.unit_price || 0),
          current_price_discount: Number(line.unit_price_inc_tax || 0),
          discount_amount: Number(line.discount_amount || 0),
          discount_type: line.discount_type || 'fixed',
          enable_stock: Number(line.enable_stock || 0),
          unit_id: line.unit_id ?? 1,
          qty_available: 0,
          dpp_inc_tax: Number(line.dpp_inc_tax || 0),
          sub_sku: line.sku || '',
          data_price_group: [],
        }),
        current_price: Number(line.unit_price || baseProduct?.current_price || 0),
        current_price_discount: Number(line.unit_price_inc_tax || baseProduct?.current_price_discount || 0),
        quantitySelected: Number(line.qty || 1),
        name: line.product_name || baseProduct?.name || 'Producto',
        sub_sku: line.sku || baseProduct?.sub_sku || '',
      } as CartItem;
    });

    setEditingInvoiceId(invoice.id);
    setSelectedCustomerId(String(invoice.contact_id || ''));
    setCartItems(mappedCartItems);
    setDraftSyncMode(getInvoiceKind(invoice));
    setNotes(invoice.notes || '');
    setActiveTab('draft');
  };

  const removeOfflineInvoice = async (invoiceId: number) => {
    const confirmed = window.confirm(`Esta seguro de eliminar la factura #${invoiceId}?`);
    if (!confirmed) {
      return;
    }

    await deleteOfflineInvoice(invoiceId);
    if (editingInvoiceId === invoiceId) {
      resetDraft();
    }
    await refreshOfflineInvoices();
  };

  const buildSyncPayload = (invoice: OfflineInvoice, lines: OfflineInvoiceLine[], syncMode: OfflineSyncMode) => {
    const parsedPayload = (() => {
      try {
        return invoice.payload_json ? JSON.parse(invoice.payload_json) : null;
      } catch (error) {
        return null;
      }
    })();

    const fallbackProducts = lines.map((line) => {
      const parsedLine = parseLineJson(line);
      if (parsedLine?.line_payload) {
        return parsedLine.line_payload;
      }

      return {
        quantity: Number(line.qty || 0),
        variation_id: line.variation_id,
        product_id: line.product_id,
        line_discount_type: line.discount_type || 'fixed',
        line_discount_amount: Number(line.discount_amount || 0),
        unit_price: Number(line.unit_price || 0),
        unit_price_inc_tax: Number(line.unit_price_inc_tax || 0),
        item_tax: 0,
        tax_id: null,
        tax_rate_id: null,
        enable_stock: Number(line.enable_stock || 0) === 1,
        product_type: line.product_type || 'single',
        base_unit_multiplier: 1,
        sub_unit_id: line.unit_id ?? 1,
        product_unit_id: line.unit_id ?? 1,
        dpp_inc_tax: Number(line.dpp_inc_tax || 0),
        unit_cost: Number(line.dpp_inc_tax || 0),
      };
    });

    const payload = parsedPayload || {
      user_id: getLoginApp()?.id ?? null,
      contact_id: invoice.contact_id,
      status: invoice.status_sale || 'quotation',
      is_quotation: 1,
      final_total: Number(invoice.total || 0),
      location_id: invoice.location_id ?? 1,
      additional_notes: invoice.notes || '',
      typeOption: 'saveOrder',
      action: 'create',
      products: fallbackProducts,
    };

    if (syncMode === 'preventa') {
      return {
        ...payload,
        status: 'draft',
        is_quotation: 0,
        type_preventa: 1,
      };
    }

    return {
      ...payload,
      status: 'quotation',
      is_quotation: 1,
      type_preventa: null,
      sub_status: 'quotation',
    };
  };

  const syncOneInvoice = async (invoiceId: number, syncMode: OfflineSyncMode = 'cotizacion') => {
    const token = getToken();
    const invoiceResult = await queryOfflineInvoiceById(invoiceId);
    const linesResult = await queryOfflineInvoiceLines(invoiceId);
    const invoice = (invoiceResult?.values || [])[0] as OfflineInvoice;
    const lines = (linesResult?.values || []) as OfflineInvoiceLine[];

    if (!invoice) {
      throw new Error('Factura offline no encontrada');
    }

    const payload = buildSyncPayload(invoice, lines, syncMode);

    await markOfflineInvoiceSyncStatus(invoiceId, 'syncing', `Enviando ${syncMode}...`, null, '');
    await refreshOfflineInvoices();

    const response = await fetch(`${getBaseUrl()}/api/appapk/syncOfflineInvoice`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: 'Bearer ' + token } : {}),
      },
      body: JSON.stringify({
        local_id: invoiceId,
        sync_mode: syncMode,
        payload,
      }),
    });

    const rawResponse = await response.text();
    let result: any = null;

    try {
      result = rawResponse ? JSON.parse(rawResponse) : null;
    } catch (error) {
      result = null;
    }

    if (!response.ok || (result && result.success === false)) {
      const backendMessage = result?.msg || result?.message || rawResponse || `HTTP ${response.status}`;
      await markOfflineInvoiceSyncStatus(invoiceId, 'error', rawResponse || '', null, backendMessage);
      throw new Error(backendMessage);
    }

    const createdCustomer = result?.created_customer;
    if (createdCustomer && Number(createdCustomer?.local_customer_id || 0) > 0 && Number(createdCustomer?.contact_db_id || 0) > 0) {
      await updateCustomerOfflineAfterSync(
        Number(createdCustomer.local_customer_id),
        Number(createdCustomer.contact_db_id),
        String(createdCustomer.contact_id || '')
      );
    }

    await deleteOfflineInvoice(invoiceId);
    const customersResult = await queryAllCustomersOffline();
    setCustomers((customersResult?.values || []) as OfflineCustomer[]);
    await refreshOfflineInvoices();
  };

  const syncLocalCustomers = async () => {
    if (!navigator.onLine) {
      window.alert('Sin conexion a internet. Intente cuando haya internet.');
      return;
    }

    const token = getToken();
    const localCustomersResult = await queryLocalCreatedCustomersOffline();
    const localCustomers = (localCustomersResult?.values || []) as Array<OfflineCustomer & { is_local_created?: number; mobile?: string }>;

    if (localCustomers.length === 0) {
      window.alert('No hay clientes locales pendientes por sincronizar.');
      return;
    }

    setIsSyncingCustomers(true);
    setSyncCustomerMessage('Sincronizando clientes...');

    const payload = {
      user_id: getLoginApp()?.id ?? 1,
      customers: localCustomers.map((customer) => ({
        local_id: customer.id,
        customer: {
          contact_id: customer.contact_id,
          name: customer.name,
          type: 'customer',
          customer_group_id: customer.customer_group_id ?? null,
          supplier_business_name: customer.supplier_business_name ?? null,
          selling_price_group_id: customer.selling_price_group_id ?? null,
          mobile: customer.mobile ?? null,
        },
      })),
    };

    const response = await fetch(`${getBaseUrl()}/api/appapk/syncOfflineCustomers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: 'Bearer ' + token } : {}),
      },
      body: JSON.stringify(payload),
    });

    const rawResponse = await response.text();
    let result: any = null;
    try {
      result = rawResponse ? JSON.parse(rawResponse) : null;
    } catch (error) {
      result = null;
    }

    if (!response.ok || !result) {
      setSyncCustomerMessage(`Error al sincronizar clientes. HTTP ${response.status}`);
      setIsSyncingCustomers(false);
      return;
    }

    const rows = Array.isArray(result?.results) ? result.results : [];
    let ok = 0;
    let failed = 0;

    for (const row of rows) {
      const localId = Number(row?.local_id || 0);
      if (Number(row?.success || 0) === 1 && localId > 0) {
        ok += 1;
        await deleteCustomerOfflineById(localId);
      } else {
        failed += 1;
      }
    }

    const customersResult = await queryAllCustomersOffline();
    setCustomers((customersResult?.values || []) as OfflineCustomer[]);

    if (selectedCustomerId) {
      const exists = (customersResult?.values || []).some((customer: any) => String(customer.id) === String(selectedCustomerId));
      if (!exists) {
        setSelectedCustomerId('');
      }
    }

    setSyncCustomerMessage(`Clientes sincronizados. Exitosos: ${ok} | Fallidos: ${failed}`);
    setIsSyncingCustomers(false);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" text="" />
          </IonButtons>
          <IonTitle>POS OFFLINE</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div style={{ padding: '16px', background: '#f4f7fb', minHeight: '100%' }}>
          <IonSegment value={activeTab} onIonChange={(event) => setActiveTab((event.detail.value as 'draft' | 'invoices') || 'draft')}>
            <IonSegmentButton value="draft">
              <IonLabel>Nueva factura</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="invoices">
              <IonLabel>Guardadas</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {activeTab === 'draft' ? (
            <>
              <IonRow style={{ marginBottom: '12px' }}>
                <IonCol size="12">
                  <div><strong>Clientes locales:</strong> {customers.length}</div>
                  <div><strong>Productos locales:</strong> {products.length}</div>
                  <div><strong>Factura editando:</strong> {editingInvoiceId ? `#${editingInvoiceId}` : 'Nueva'}</div>
                </IonCol>
              </IonRow>

              <IonItem>
                <IonLabel position="stacked">Cliente</IonLabel>
                <div style={{ width: '100%', marginTop: '8px' }}>
                  <Select
                    placeholder="Seleccione cliente"
                    isSearchable={true}
                    menuPortalTarget={document.querySelector('body')}
                    options={customerOptions}
                    value={customerOptions.find((option) => option.value === selectedCustomerId) || null}
                    onChange={(option: any) => setSelectedCustomerId(option?.value || '')}
                    styles={{
                      menu: (provided) => ({ ...provided, zIndex: 9999, color: 'black' }),
                      menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                    }}
                  />
                </div>
              </IonItem>

              <IonButton
                fill="outline"
                size="small"
                style={{ marginTop: '8px' }}
                onClick={() => setIsCustomerModalOpen(true)}
              >
                <IonIcon icon={personAddOutline} slot="start" />
                Nuevo cliente
              </IonButton>

              <IonItem>
                <IonLabel position="stacked">Producto</IonLabel>
                <div style={{ width: '100%', marginTop: '8px' }}>
                  <Select
                    placeholder="Buscar producto por nombre o SKU"
                    isSearchable={true}
                    menuPortalTarget={document.querySelector('body')}
                    options={productOptions}
                    value={productOptions.find((option) => option.value === selectedProductId) || null}
                    onChange={(option: any) => setSelectedProductId(option?.value || '')}
                    styles={{
                      menu: (provided) => ({ ...provided, zIndex: 9999, color: 'black' }),
                      menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                    }}
                  />
                </div>
              </IonItem>

              {products.length === 0 ? (
                <div style={{ color: '#b00020', fontSize: '12px', marginTop: '8px' }}>
                  No hay productos en la base local. Primero sincronice productos desde la pantalla de Backup.
                </div>
              ) : null}

              <IonButton expand="block" onClick={addSelectedProductToCart} style={{ marginTop: '12px' }}>
                Agregar al carrito
              </IonButton>

              <IonItem style={{ marginTop: '8px' }}>
                <IonLabel position="stacked">Nota de factura</IonLabel>
                <IonInput
                  value={notes}
                  onIonChange={(event) => setNotes(event.detail.value || '')}
                  placeholder="Escriba una nota"
                />
              </IonItem>

              <IonItem style={{ marginTop: '8px' }} lines="none">
                <IonLabel position="stacked">Tipo de factura</IonLabel>
                <IonRadioGroup
                  value={draftSyncMode}
                  onIonChange={(event) => setDraftSyncMode((event.detail.value as OfflineSyncMode) || 'cotizacion')}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <IonItem lines="none" style={{ '--inner-padding-end': '0px', '--padding-start': '0px' } as any}>
                      <IonRadio slot="start" value="cotizacion" />
                      <IonLabel>Cotizacion</IonLabel>
                    </IonItem>
                    <IonItem lines="none" style={{ '--inner-padding-end': '0px', '--padding-start': '0px' } as any}>
                      <IonRadio slot="start" value="preventa" />
                      <IonLabel>Preventa</IonLabel>
                    </IonItem>
                  </div>
                </IonRadioGroup>
              </IonItem>
              <div style={{ fontSize: '12px', color: '#475569', marginTop: '6px' }}>
                Tipo seleccionado: {draftSyncMode === 'preventa' ? 'Preventa' : 'Cotizacion'}
              </div>

              <div
                style={{
                  marginTop: '20px',
                  background: '#ffffff',
                  borderRadius: '18px',
                  padding: '16px',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
                  border: '1px solid #e6edf5'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#16324f' }}>Carrito offline</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Revise cantidades y total antes de generar la orden</div>
                  </div>
                  <div
                    style={{
                      background: '#e8f1ff',
                      color: '#1565c0',
                      borderRadius: '999px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: 700
                    }}
                  >
                    {cartItems.length} item(s)
                  </div>
                </div>

                {cartItems.length === 0 ? (
                  <div
                    style={{
                      background: '#f8fafc',
                      border: '1px dashed #cbd5e1',
                      borderRadius: '14px',
                      padding: '18px',
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: '14px'
                    }}
                  >
                    El carrito esta vacio. Seleccione un producto y agreguelo.
                  </div>
                ) : (
                  <IonList style={{ background: 'transparent', padding: 0 }}>
                    {cartItems.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          background: '#f8fbff',
                          border: '1px solid #dbe7f3',
                          borderRadius: '16px',
                          padding: '14px',
                          marginBottom: '12px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, color: '#16324f', fontSize: '15px' }}>{item.name}</div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                              SKU: {item.sub_sku || 'N/D'}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                              <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 600 }}>
                                Precio: {formatPrimaryCurrency(item.current_price_discount || 0)}
                              </span>
                              <span style={{ background: '#ecfdf5', color: '#047857', borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 600 }}>
                                Stock: {Number(item.qty_available || 0)}
                              </span>
                            </div>
                          </div>

                          <IonButton color="danger" fill="clear" onClick={() => updateCartQuantity(item.id, 0)}>
                            Quitar
                          </IonButton>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '14px' }}>
                          <div style={{ minWidth: '90px' }}>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Cantidad</div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                            >
                              <IonButton
                                size="small"
                                fill="outline"
                                onClick={() => updateCartQuantity(item.id, Math.max(1, Number(item.quantitySelected || 1) - 1))}
                              >
                                -
                              </IonButton>
                              <IonInput
                                type="number"
                                min="1"
                                value={item.quantitySelected}
                                onIonChange={(event) => updateCartQuantity(item.id, Number(event.detail.value || 1))}
                                style={{
                                  maxWidth: '70px',
                                  background: '#ffffff',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '10px',
                                  paddingLeft: '10px',
                                  textAlign: 'center'
                                }}
                              />
                              <IonButton
                                size="small"
                                fill="outline"
                                onClick={() => updateCartQuantity(item.id, Number(item.quantitySelected || 0) + 1)}
                              >
                                +
                              </IonButton>
                            </div>
                          </div>

                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Subtotal</div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                              {formatPrimaryCurrency(Number(item.current_price_discount || 0) * Number(item.quantitySelected || 0))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </IonList>
                )}

                <IonRow style={{ marginTop: '8px' }}>
                  <IonCol size="12">
                    <div
                      style={{
                        background: '#16324f',
                        color: '#ffffff',
                        borderRadius: '16px',
                        padding: '16px'
                      }}
                    >
                      <div style={{ fontSize: '13px', opacity: 0.85 }}>Cliente seleccionado</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '4px' }}>
                        {selectedCustomer ? `${selectedCustomer.contact_id} - ${selectedCustomer.name}` : 'Sin seleccionar'}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px' }}>
                        <span style={{ fontSize: '14px', opacity: 0.9 }}>Total general</span>
                        <span style={{ fontSize: '24px', fontWeight: 800 }}>{formatPrimaryCurrency(cartTotal)}</span>
                      </div>
                    </div>
                  </IonCol>
                </IonRow>
              </div>

              <IonButton expand="block" color="tertiary" onClick={() => saveOfflineInvoice(false)} style={{ marginTop: '8px' }}>
                {editingInvoiceId ? 'Actualizar factura offline' : 'Guardar factura offline'}
              </IonButton>

              <IonButton expand="block" color="warning" onClick={() => saveOfflineInvoice(true)} style={{ marginTop: '8px' }}>
                {editingInvoiceId ? 'Actualizar e imprimir' : 'Guardar e imprimir'}
              </IonButton>

              {editingInvoiceId ? (
                <IonButton
                  expand="block"
                  color="medium"
                  fill="outline"
                  onClick={resetDraft}
                  style={{ marginTop: '8px' }}
                >
                  Cancelar edicion
                </IonButton>
              ) : null}
            </>
          ) : (
            <>
              {offlineInvoices.length === 0 ? (
                <div
                  style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    border: '1px dashed #cbd5e1',
                    padding: '14px',
                    color: '#475569'
                  }}
                >
                  No hay facturas offline guardadas.
                </div>
              ) : (
                <IonList style={{ background: 'transparent', padding: 0 }}>
                  {offlineInvoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      style={{
                        background: '#ffffff',
                        borderRadius: '14px',
                        border: '1px solid #d8e1ec',
                        padding: '14px',
                        marginBottom: '12px'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 700, color: '#16324f' }}>{getInvoiceKind(invoice) === 'cotizacion' ? 'Cotización' : 'Pre-Venta'} #{invoice.id}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{invoice.customer_name || 'Cliente no definido'}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                            Lineas: {Number(invoice.lines_total || 0)} | Total: {formatPrimaryCurrency(invoice.total || 0)}
                          </div>
                          <div style={{ fontSize: '12px', marginTop: '2px', color: invoice.status_sync === 'synced' ? '#047857' : invoice.status_sync === 'error' ? '#b91c1c' : '#1d4ed8' }}>
                            Sync: {invoice.status_sync || 'pending'}
                          </div>
                          {invoice.error_message ? (
                            <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '2px' }}>
                              Error: {invoice.error_message}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: '10px',
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap',
                          alignItems: 'center'
                        }}
                      >
                        <IonButton fill="outline" size="small" onClick={() => editOfflineInvoice(invoice.id)}>
                          <IonIcon icon={createOutline} slot="start" />
                          Editar
                        </IonButton>

                        <IonButton fill="outline" size="small" onClick={() => openInvoiceModal(invoice.id)}>
                          <IonIcon icon={eyeOutline} slot="start" />
                          Ver
                        </IonButton>

                        <IonButton
                          fill="outline"
                          size="small"
                          color="warning"
                          onClick={async () => {
                            const linesResult = await queryOfflineInvoiceLines(invoice.id);
                            printInvoice(invoice, (linesResult?.values || []) as OfflineInvoiceLine[]);
                          }}
                        >
                          <IonIcon icon={printOutline} slot="start" />
                          Imprimir
                        </IonButton>

                        <IonButton
                          fill="outline"
                          size="small"
                          color="danger"
                          onClick={() => removeOfflineInvoice(invoice.id)}
                          disabled={isSyncing}
                        >
                          <IonIcon icon={trashOutline} slot="start" />
                          Eliminar
                        </IonButton>
                      </div>
                    </div>
                  ))}
                </IonList>
              )}
            </>
          )}

          <IonModal isOpen={isInvoiceModalOpen} onDidDismiss={() => setIsInvoiceModalOpen(false)}>
            <IonHeader>
              <IonToolbar color="primary">
                <IonTitle>Factura Offline</IonTitle>
                <IonButtons slot="end">
                  <IonButton onClick={() => setIsInvoiceModalOpen(false)}>Cerrar</IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent>
              <div style={{ padding: '16px' }}>
                {modalInvoice ? (
                  <>
                    <div style={{ fontWeight: 700, fontSize: '18px', marginBottom: '8px' }}>Factura #{modalInvoice.id}</div>
                    <div style={{ marginBottom: '6px' }}><strong>Cliente:</strong> {modalInvoice.customer_name || 'N/D'}</div>
                    <div style={{ marginBottom: '6px' }}><strong>Estado sync:</strong> {modalInvoice.status_sync || 'pending'}</div>
                    <div style={{ marginBottom: '6px' }}><strong>Nota:</strong> {modalInvoice.notes || ''}</div>
                    <div style={{ marginBottom: '12px' }}><strong>Total:</strong> {formatPrimaryCurrency(modalInvoice.total || 0)}</div>

                    <IonList>
                      {modalInvoiceLines.map((line) => (
                        <IonItem key={line.id}>
                          <IonLabel>
                            <h3>{line.product_name || 'Producto'}</h3>
                            <p>SKU: {line.sku || 'N/D'}</p>
                            <p>Cant: {Number(line.qty || 0)} x {formatPrimaryCurrency(line.unit_price_inc_tax || 0)}</p>
                          </IonLabel>
                        </IonItem>
                      ))}
                    </IonList>

                    <IonButton
                      expand="block"
                      color="warning"
                      onClick={() => printInvoice(modalInvoice, modalInvoiceLines)}
                      style={{ marginTop: '12px' }}
                    >
                      Reimprimir
                    </IonButton>
                  </>
                ) : (
                  <div>No hay factura seleccionada</div>
                )}
              </div>
            </IonContent>
          </IonModal>

          <IonModal isOpen={isCustomerModalOpen} onDidDismiss={() => setIsCustomerModalOpen(false)}>
            <IonHeader>
              <IonToolbar color="primary">
                <IonTitle>Nuevo cliente local</IonTitle>
                <IonButtons slot="end">
                  <IonButton onClick={() => setIsCustomerModalOpen(false)}>Cerrar</IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent>
              <div style={{ padding: '16px' }}>
                <IonItem>
                  <IonLabel position="stacked">Nombre *</IonLabel>
                  <IonInput
                    value={newCustomerName}
                    onIonChange={(event) => setNewCustomerName(event.detail.value || '')}
                    placeholder="Nombre del cliente"
                  />
                </IonItem>

                <IonItem>
                  <IonLabel position="stacked">Movil *</IonLabel>
                  <IonInput
                    value={newCustomerMobile}
                    onIonChange={(event) => setNewCustomerMobile(event.detail.value || '')}
                    placeholder="Ej: 8888-9999"
                  />
                </IonItem>

                <IonItem>
                  <IonLabel position="stacked">Nombre comercial</IonLabel>
                  <IonInput
                    value={newCustomerBusinessName}
                    onIonChange={(event) => setNewCustomerBusinessName(event.detail.value || '')}
                    placeholder="Opcional"
                  />
                </IonItem>

                <IonButton expand="block" style={{ marginTop: '12px' }} onClick={saveLocalCustomer}>
                  Guardar cliente
                </IonButton>
              </div>
            </IonContent>
          </IonModal>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PosOffline;